import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/store';
import { getCurrentProjectId } from 'src/app/projects/views/project-list/state/projectListSelector';
import useProject from 'src/shared/hooks/useProject';
import { Breadcrumb } from 'src/shared/components/breadcrumbs/Breadcrumbs';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import Show from 'src/shared/components/show/Show';
import SnackbarAlert from 'src/shared/components/snackbar-alert/SnackbarAlert';
import Loader from 'src/shared/components/loader/Loader';
import { ErrorMessageWithBackground } from 'src/shared/components/error-msg/ErrorMessage';
import useFiles from 'src/shared/hooks/useFiles';
import useBoolean from 'src/shared/hooks/useBoolean';
import { UploaderType, SiteFileType } from 'src/shared/types';
import { Status, statusToAlertColor } from 'src/shared/types';
import UploadFileModal from './widgets/UploadFileModal';
import useSiteFilesAnalytics from './hooks/useSiteFilesAnalytics';
import FilesFilters from './widgets/FilesFilters';
import FilesTable from './widgets/FilesTable';
import EmptyFilesState from './widgets/EmptyFilesState';
import { setSearchText, setUploadedByFilter, getSearchText, getUploadedByFilter } from './state/siteFilesSlice';

const SiteFiles = () => {
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const currentProjectId = useAppSelector(getCurrentProjectId);
    const { currentProjectDetails } = useProject();
    const [isUploadModalOpen, uploadModal] = useBoolean(false);
    const [snackbar, setSnackbar] = React.useState<{ open: boolean; message: string; severity: Status }>({
        open: false,
        message: '',
        severity: Status.SUCCESS,
    });

    const searchText = useAppSelector(getSearchText);
    const uploadedByFilter = useAppSelector(getUploadedByFilter);
    const filterFileType = searchParams.get('filterFileType') || '';

    const selectedFileTypeParams = React.useMemo(() => {
        const values = filterFileType
            .split(',')
            .map(v => v.trim())
            .filter(v => v === 'images' || v === 'csv' || v === 'pdf' || v === 'other');
        return new Set(values);
    }, [filterFileType]);

    const fileTypeFilter: Record<SiteFileType, boolean> = React.useMemo(
        () => ({
            [SiteFileType.ImageFiles]: selectedFileTypeParams.has('images'),
            [SiteFileType.CsvAndxlsxFiles]: selectedFileTypeParams.has('csv'),
            [SiteFileType.PdfFiles]: selectedFileTypeParams.has('pdf'),
            [SiteFileType.OtherFiles]: selectedFileTypeParams.has('other'),
        }),
        [selectedFileTypeParams]
    );

    const {
        files: siteFiles = [],
        isRefetching,
        isLoading,
        error,
        upload,
        rename,
        removeOne,
        downloadMultipleFiles,
    } = useFiles({ projectId: currentProjectId || undefined });

    const { trackSingleFileDownload, trackMultipleFilesDownload, trackUploadSuccess, trackUploadError, trackRename, trackDelete } =
        useSiteFilesAnalytics();

    const breadcrumbs: Breadcrumb[] = [
        { label: 'My Sites', callback: () => window.history.back() },
        { label: currentProjectDetails?.projectName || '' },
        { label: 'Files' },
    ];

    const handleShowSnackbar = (message: { message: string; severity: Status }) => {
        setSnackbar({
            open: true,
            ...message,
        });
    };

    const handleUpload = async (files: File[]) => {
        const result = await upload(files);
        if (result.isSuccess) {
            setSnackbar({
                open: true,
                message: result.message,
                severity: Status.SUCCESS,
            });
            trackUploadSuccess({ count: files.length });
            uploadModal.off();
        } else {
            setSnackbar({
                open: true,
                message: 'Failed to upload files. Please try again.',
                severity: Status.ERROR,
            });
            trackUploadError({ message: result.message });
        }
        return result;
    };

    const filters = (
        <FilesFilters
            searchText={searchText}
            onSearchChange={value => dispatch(setSearchText(value))}
            uploadedByFilter={uploadedByFilter}
            onUploadedByChange={vals => {
                dispatch(setUploadedByFilter({ key: UploaderType.NatureMetrics, value: vals.includes(UploaderType.NatureMetrics) }));
                dispatch(setUploadedByFilter({ key: UploaderType.Client, value: vals.includes(UploaderType.Client) }));
            }}
            fileTypeFilter={fileTypeFilter}
            onFileTypeChange={vals => {
                const next = new URLSearchParams(searchParams);
                const selected = [
                    vals.includes(SiteFileType.ImageFiles) ? 'images' : null,
                    vals.includes(SiteFileType.CsvAndxlsxFiles) ? 'csv' : null,
                    vals.includes(SiteFileType.PdfFiles) ? 'pdf' : null,
                    vals.includes(SiteFileType.OtherFiles) ? 'other' : null,
                ].filter(Boolean);

                if (selected.length === 0) {
                    next.delete('filterFileType');
                } else {
                    next.set('filterFileType', selected.join(','));
                }
                setSearchParams(next);
            }}
            onUploadClick={uploadModal.on}
        />
    );

    const handleRenameMutation = async (fileId: string, newName: string) => {
        await rename(fileId, newName);
    };

    const handleDeleteMutation = async (fileId: string) => {
        await removeOne(fileId);
    };

    const mainContent = (
        <Show
            when={siteFiles.length > 0}
            fallback={
                isLoading || isRefetching ? (
                    <Loader />
                ) : error ? (
                    <ErrorMessageWithBackground message="We couldn't load this site's files. Please refresh the page or try again later." />
                ) : (
                    <EmptyFilesState onUploadClick={uploadModal.on} textContent='This site has no files yet.' />
                )
            }
        >
            <FilesTable
                projectId={currentProjectId || ''}
                files={siteFiles}
                searchText={searchText}
                uploadedByFilter={uploadedByFilter}
                fileTypeFilter={fileTypeFilter}
                renameFileMutation={handleRenameMutation}
                deleteFileMutation={handleDeleteMutation}
                onShowSnackbar={handleShowSnackbar}
                downloadMultipleFiles={downloadMultipleFiles}
                onSingleDownload={params => trackSingleFileDownload(params)}
                onBulkDownload={params => trackMultipleFilesDownload(params)}
                onRename={params => trackRename(params)}
                onDelete={params => trackDelete(params)}
            />
        </Show>
    );

    return (
        <>
            <StandardAppLayout title='Files' breadcrumbs={breadcrumbs} mainContent={mainContent} filterSection={filters} />
            {isUploadModalOpen && (
                <UploadFileModal
                    onClose={uploadModal.off}
                    onUpload={handleUpload}
                    description='Upload site-related documentation which will be accessible to all site users.'
                />
            )}
            <SnackbarAlert
                open={snackbar.open}
                message={snackbar.message}
                severity={statusToAlertColor(snackbar.severity)}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                autoHideDuration={6000}
            />
        </>
    );
};

export default SiteFiles;
